import { useTranslation } from 'react-i18next';
import { HiMiniCheck as CheckIcon } from 'react-icons/hi2';
import {
  appWidth,
  Button,
  Divider,
  presetNames,
  RadioCardGroup,
  Select,
  type AppWidth as AppWidthType,
  type ThemeMode
} from '@knack/asterisk-react';

import { cn } from '@/utils/tailwind';
import { Section } from '@/pages/themes/theme-editor/Section';
import { useThemesPageContext } from '@/pages/themes/ThemesPageContext';

// Keep the TW colors here, otherwise the parser won't pick them up
// and the interpolation in the button below will not work
const presetColors = [
  'bg-brand',
  'bg-blue-500',
  'bg-cyan-500',
  'bg-lime-500',
  'bg-green-500',
  'bg-pink-500',
  'bg-fuchsia-500',
  'bg-violet-500',
  'bg-amber-500'
] as const;

export function General() {
  const [t] = useTranslation();
  const { theme, setThemeField } = useThemesPageContext();
  const themeModeOptions = [
    {
      id: 'light',
      title: t('themes.light_mode')
    },
    {
      id: 'dark',
      title: t('themes.dark_mode')
    },
    {
      id: 'system',
      title: t('themes.system'),
      description: t('themes.system_description')
    }
  ];

  return (
    <>
      <Section sectionNameKey="appearance">
        <div className="my-4 flex flex-wrap gap-2">
          {presetNames.map((preset, i) => (
            <Button
              intent="minimal"
              key={preset}
              title={preset}
              className={cn(
                `size-6 rounded-full border border-default text-white ${presetColors[i]} hover:${presetColors[i]} active:${presetColors[i]} focus:${presetColors[i]}`
              )}
              onClick={() =>
                setThemeField((draft) => {
                  draft.colors.preset = preset;
                })
              }
            >
              {theme.colors.preset === preset && (
                <>
                  <Button.Icon icon={CheckIcon} />
                  <span className="sr-only">{t('themes.selected_preset')}</span>
                </>
              )}
            </Button>
          ))}
        </div>
        <RadioCardGroup
          onValueChange={(value) =>
            setThemeField((draft) => {
              draft.mode = value as ThemeMode;
            })
          }
          value={theme.mode}
        >
          {themeModeOptions.map((mode) => (
            <RadioCardGroup.Card key={mode.id} value={mode.id} width="100%" className="focus:z-10">
              <RadioCardGroup.Content title={mode.title} />
            </RadioCardGroup.Card>
          ))}
        </RadioCardGroup>
      </Section>
      <Divider className="my-4" />
      <Section sectionNameKey="app_width">
        <RadioCardGroup
          onValueChange={(value) =>
            setThemeField((draft) => {
              draft.appWidth = value as AppWidthType;
            })
          }
          value={theme.appWidth}
        >
          {appWidth.map((item) => (
            <RadioCardGroup.Card key={item} value={item} width="100%">
              <RadioCardGroup.Content title={t(`themes.${item}`)} />
            </RadioCardGroup.Card>
          ))}
        </RadioCardGroup>
      </Section>
      <Divider className="my-4" />
      <Section sectionNameKey="font">
        <Select disabled>
          <Select.Trigger placeholder={t('themes.default')} className="w-full rounded-lg" />
        </Select>
      </Section>
    </>
  );
}
